/* STENCIL IMPORTS */
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Event,
  EventEmitter,
  State
} from "@stencil/core";
/* OTHER LIBRARIES IMPORTS */
import { CheckedItemsInfo } from "../../_helpers/list-selector/list-selector";
/* CUSTOM IMPORTS */
import { config } from "../../../common/config";
import { Locale } from "../../../common/locale";
import { renderListItems } from "../../../common/render-list-item";
import { filesToListItemsData } from "./helpers";

@Component({
  tag: "gx-ide-bpm-import-files",
  styleUrl: "bpm-import-files.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/bpm-import-files"]
})
export class GxIdeTemplate {
  // 1.OWN PROPERTIES //

  /**
   * The component hard-coded strings translations.
   */
  private _componentLocale: any;

  private renderedFirstTime = false;

  // 2. REFERENCE TO ELEMENTS //

  @Element() el: HTMLGxIdeBpmImportFilesElement;

  private fileInput: HTMLInputElement;
  private automaticBackupEl: HTMLChCheckboxElement;

  // 3.STATE() VARIABLES //
  /**
   * Contains the list of files
   */
  @State() files: File[] = [];

  /**
   * Contains the list of selected files
   */
  @State() selectedFiles: string[] = [];

  // 4.PUBLIC PROPERTY API | WATCH'S //

  /**
   * If true it displays the component title on the header
   */
  @Prop() readonly displayTitle = false;

  /**
   * Allowed extensions when 'Add files' button is pressed
   */
  @Prop() readonly allowedExtensions: string[];

  /**
   * Callback invoked when user press 'OK' button
   */
  @Prop() readonly confirmCallback: (data: ImportData) => Promise<boolean>;

  /**
   * Callback invoked when user press 'Cancel' button.
   */
  @Prop() readonly cancelCallback: () => Promise<void>;

  // 5.EVENTS (EMIT) //

  /**
   * @description Gets fired when the component has rendered for the first time.
   */
  @Event() componentDidRenderFirstTime: EventEmitter<string>;

  /**
   * @description Gets fired when something changes on items section.
   */
  @Event() selectionChanged: EventEmitter<void>;

  // 6.COMPONENT LIFECYCLE METHODS //

  async componentWillLoad() {
    this._componentLocale = await Locale.getComponentStrings(this.el);
  }

  componentDidRender() {
    if (!this.renderedFirstTime) {
      this.componentDidRenderFirstTime.emit(
        this._componentLocale.componentName
      );
      this.renderedFirstTime = true;
    }
  }

  // 7.LISTENERS //

  // 8.PUBLIC METHODS API //

  // 9.LOCAL METHODS //

  private fileSelectionChangedHandler = (
    selectedItem: CustomEvent<CheckedItemsInfo>
  ) => {
    this.selectionChanged.emit();

    const selectedFiles = Array.isArray(selectedItem.detail)
      ? selectedItem.detail
      : [selectedItem.detail];

    this.selectedFiles = selectedFiles.map(item => item.itemValue);
  };

  private loadFileHandle = async () => {
    this.fileInput.click();
  };

  private handleFileInputChange = (event: Event) => {
    const fileInput = event.target as HTMLInputElement;

    if (fileInput.files && fileInput.files.length > 0) {
      const newFiles: File[] = Array.from(fileInput.files);
      const uniqueFiles = newFiles.filter(
        newFile =>
          !this.files.some(existingFile => existingFile.name === newFile.name)
      );
      this.files = [...this.files, ...uniqueFiles];
    }

    this.fileInput.value = null;
  };

  private confirmCallbackHandle = async () => {
    if (this.confirmCallback && this.files.length > 0) {
      const data: ImportData = {
        automaticBackup: this.automaticBackupEl.value === "true",
        files: this.files
      };

      await this.confirmCallback(data);
    }
  };

  private removeFiles = (filesToRemove: string[]) => {
    this.files = this.files.filter(file => !filesToRemove.includes(file.name));

    this.selectedFiles = [];
    this.fileInput.value = null;
  };

  private handleRemoveButtonClick = () => {
    this.removeFiles(this.selectedFiles);
  };

  // 10.RENDER() FUNCTION //

  render() {
    return (
      <Host class="gx-ide-component">
        <div class="gx-ide-main-wrapper">
          <gx-ide-container slimmerFooter={config.gxIdeContainer.slimmerFooter}>
            <div class="main">
              {/* path list*/}
              <div class="controls-wrapper controls-wrapper--vertical">
                <label htmlFor="path-list">
                  {this._componentLocale.main.listLabel}
                </label>
                <gx-ide-list-selector
                  id="path-list"
                  type="multi-selection"
                  listName="path-list"
                  ordered
                  onSelectionChanged={this.fileSelectionChangedHandler}
                  class="path-list"
                >
                  {renderListItems(filesToListItemsData(this.files))}
                </gx-ide-list-selector>
              </div>

              <div class="footer">
                <div class="checkbox-container">
                  <ch-checkbox
                    id="automatic-backup"
                    accessibleName={
                      this._componentLocale.main.automaticBackupLabel
                    }
                    checkedValue="true"
                    unCheckedValue="false"
                    value="checked"
                    ref={(el: HTMLChCheckboxElement) =>
                      (this.automaticBackupEl = el as HTMLChCheckboxElement)
                    }
                  ></ch-checkbox>
                  <label htmlFor="automatic-backup">
                    {this._componentLocale.main.automaticBackupLabel}
                  </label>
                </div>
                <div class="controls-wrapper">
                  <input
                    type="file"
                    id="input_file"
                    accept={this.allowedExtensions?.join(", ")}
                    ref={el => (this.fileInput = el as HTMLInputElement)}
                    onChange={this.handleFileInputChange}
                    class="hidden-input"
                    multiple
                  />
                  <button onClick={this.loadFileHandle}>
                    {this._componentLocale.main.addFilesButton}
                  </button>
                  <button
                    onClick={this.handleRemoveButtonClick}
                    disabled={this.selectedFiles.length === 0}
                  >
                    {this._componentLocale.main.removeFilesButton} (
                    {this.selectedFiles.length})
                  </button>
                </div>
              </div>
            </div>

            <button slot="footer-end" onClick={this.confirmCallbackHandle}>
              {this._componentLocale.footer.confirmButton}
            </button>
            <button slot="footer-end" onClick={this.cancelCallback}>
              {this._componentLocale.footer.cancelButton}
            </button>
          </gx-ide-container>
        </div>
      </Host>
    );
  }
}

export type ImportData = {
  files: File[];
  automaticBackup: boolean;
};
